import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAudioPlayer } from '../contexts/AudioPlayerContext';
import { MIXCLOUD_USERNAME, MIXCLOUD_API_BASE } from '../config/mixcloud';
import '../styles/CatchupPage.css';

/* -------------------------------------------------------------------------- */
/*  Config                                                                     */
/* -------------------------------------------------------------------------- */

const USERNAME = MIXCLOUD_USERNAME || 'utterradio';
const API_BASE = MIXCLOUD_API_BASE || 'https://api.mixcloud.com';
const SCHEDULE_URL = `${process.env.PUBLIC_URL}/playlists.json`;

const BRANDS = [
  { end: new Date('2017-09-01'), url: 'https://utter-radio.co.uk/images/UtterRadioLogoFull.jpg' },
];

const WEEKDAY_ORDER = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
const REQUIRED_DAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'];
const DAY_LABELS = {
  monday: 'Monday',
  tuesday: 'Tuesday',
  wednesday: 'Wednesday',
  thursday: 'Thursday',
  friday: 'Friday',
  saturday: 'Saturday',
  sunday: 'Sunday',
};

/* -------------------------------------------------------------------------- */
/*  Icons                                                                      */
/* -------------------------------------------------------------------------- */

const SearchIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
    <path d="M15.5 14h-.79l-.28-.27A6.471 6.471 0 0 0 16 9.5 6.5 6.5 0 1 0 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z" />
  </svg>
);
const CloseIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
    <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
  </svg>
);
const ShareIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
    <path d="M3.9 12c0-1.71 1.39-3.1 3.1-3.1h4V7H7c-2.76 0-5 2.24-5 5s2.24 5 5 5h4v-1.9H7c-1.71 0-3.1-1.39-3.1-3.1zM8 13h8v-2H8v2zm9-6h-4v1.9h4c1.71 0 3.1 1.39 3.1 3.1s-1.39 3.1-3.1 3.1h-4V17h4c2.76 0 5-2.24 5-5s-2.24-5-5-5z" />
  </svg>
);

const PlayPauseCircle = ({ playing = false, size = 36 }) => (
  <svg
    width={`${size}px`}
    height={`${size}px`}
    viewBox="0 0 45.9 45.9"
    fill="#BD003A"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden
  >
    <g transform="translate(0, 0) scale(0.08)" style={{ display: playing ? 'none' : 'inline' }}>
      <g>
        <circle cx="250" cy="250" r="200" fill="white" />
        <path
          d="M229.5,0C102.751,0,0,102.751,0,229.5S102.751,459,229.5,459S459,356.249,459,229.5S356.249,0,229.5,0z M310.292,239.651 l-111.764,76.084c-3.761,2.56-8.63,2.831-12.652,0.704c-4.022-2.128-6.538-6.305-6.538-10.855V153.416 c0-4.55,2.516-8.727,6.538-10.855c4.022-2.127,8.891-1.857,12.652,0.704l111.764,76.084c3.359,2.287,5.37,6.087,5.37,10.151 C315.662,233.564,313.652,237.364,310.292,239.651z"
          fill="#BD003A"
        />
      </g>
    </g>
    <g transform="translate(0, 0) scale(0.072)" style={{ display: playing ? 'inline' : 'none' }}>
      <g>
        <circle cx="250" cy="250" r="200" fill="white" />
        <path
          d="M256,0C114.617,0,0,114.617,0,256s114.617,256,256,256 256-114.617,256-256S397.383,0,256,0z M336,320c0,8.836-7.156,16-16,16H192c-8.844,0-16-7.164-16-16V192c0-8.836,7.156-16,16-16h128c8.844,0,16,7.164,16,16V320z"
          fill="#BD003A"
        />
      </g>
    </g>
  </svg>
);

/* -------------------------------------------------------------------------- */
/*  Helpers                                                                    */
/* -------------------------------------------------------------------------- */

const formatDuration = (secs) => {
  if (!secs) return '0:00';
  const h = Math.floor(secs / 3600);
  const m = Math.floor((secs % 3600) / 60);
  const s = secs % 60;
  if (h > 0) return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  return `${m}:${String(s).padStart(2, '0')}`;
};

const parseShow = (show) => {
  let artwork = show.pictures?.large || '';
  const isDefaultArtwork =
    artwork.includes('profile/6/b/0/b/556b') ||
    artwork.includes('default') ||
    artwork.includes('1f69-0bcf-406e-b29c-54b46dd8a152');

  if (isDefaultArtwork || !artwork) {
    const created = new Date(show.created_time);
    const era = BRANDS.find((b) => created <= b.end);
    artwork = era ? era.url : BRANDS[BRANDS.length - 1].url;
  }

  let dateLabel = '';
  const dateMatch = show.name?.match(/(\d{1,2})[/.](\d{1,2})[/.](\d{2,4})/);
  if (dateMatch) {
    const d = dateMatch[1].padStart(2, '0');
    const m = dateMatch[2].padStart(2, '0');
    const y = dateMatch[3].length === 2 ? `20${dateMatch[3]}` : dateMatch[3];
    dateLabel = `${d}/${m}/${y}`;
  }

  let rawName = (show.name || '')
    .replace(/#\w+/g, '')
    .replace(/\(\d+[/.]\d+[/.]\d+\)/g, '')
    .replace(/-?\s*\d+[/.]\d+[/.]\d+/g, '')
    .replace(/\s-\s/g, ' ')
    .trim();

  let showTitle = rawName;
  let presenter = '';
  let subtitle = '';

  const bracketMatch = rawName.match(/\((.*?)\)/);
  if (bracketMatch) {
    subtitle = bracketMatch[1].trim();
    showTitle = rawName.replace(/\(.*?\)/g, '').trim();
  }

  if (showTitle.includes(':')) {
    const parts = showTitle.split(':');
    showTitle = parts[0].trim();
    const rest = parts.slice(1).join(':').trim();
    subtitle = subtitle ? `${rest} (${subtitle})` : rest;
  }

  const splitMatch = showTitle.match(/ (WITH|W\/) (.*)/i);
  if (splitMatch) {
    presenter = splitMatch[2].trim();
    showTitle = showTitle.split(/ (WITH|W\/) /i)[0].trim();
  }

  return {
    artwork,
    dateLabel,
    title: (showTitle || 'Untitled').toUpperCase(),
    presenter,
    subtitle,
  };
};

/** Derive a match pattern from a title (first 2 significant words). */
const deriveMatchPattern = (title) => {
  const stop = new Set(['the', 'a', 'an', 'of', 'and', '&', 'with', 'show', 'radio', 'on', 'in']);
  return (title || '')
    .toLowerCase()
    .replace(/[^\w\s]/g, ' ')
    .split(/\s+/)
    .filter((w) => w && !stop.has(w))
    .slice(0, 2)
    .join(' ');
};

const getDaysForScheduleEntry = (scheduleEntry) => {
  if (!scheduleEntry) return [];
  if (Array.isArray(scheduleEntry.days)) {
    return scheduleEntry.days
      .map((d) => String(d).toLowerCase())
      .filter((d) => WEEKDAY_ORDER.includes(d));
  }
  if (scheduleEntry.dayOfWeek) {
    return [String(scheduleEntry.dayOfWeek).toLowerCase()].filter((d) =>
      WEEKDAY_ORDER.includes(d)
    );
  }
  return [];
};

/* -------------------------------------------------------------------------- */
/*  Component                                                                  */
/* -------------------------------------------------------------------------- */

export const CatchupPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const {
    isPlaying: globalIsPlaying,
    isCatchupMode,
    loadMixcloudEpisode,
  } = useAudioPlayer();

  /* ---------------- State ---------------- */
  const [allCloudcasts, setAllCloudcasts] = useState([]);
  const [scheduleShows, setScheduleShows] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const [selectedDay, setSelectedDay] = useState(
    () => searchParams.get('day') || 'all'
  );
  const [selectedShowKey, setSelectedShowKey] = useState(
    () => searchParams.get('show') || null
  );
  const [playingKey, setPlayingKey] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  /* ---------------- Refs ---------------- */
  const hasFetchedRef = useRef(false);
  const restoredShowRef = useRef(false);
  const restoredEpRef = useRef(false);

  /* ---------------- Initial data fetch ---------------- */
  useEffect(() => {
    if (hasFetchedRef.current) return;
    let isActive = true;

    (async () => {
      setIsLoading(true);
      setError(null);

      try {
        // 1. Fetch schedule (admin-provided day mapping)
        let schedShows = [];
        try {
          const res = await fetch(SCHEDULE_URL, { cache: 'no-store' });
          if (res.ok) {
            const data = await res.json();
            if (Array.isArray(data?.shows)) schedShows = data.shows;
          }
        } catch (err) {
          console.warn('playlists.json not available — showing all shows only:', err);
        }

        // 2. Fetch all cloudcasts (paginated)
        let cloudcasts = [];
        let nextUrl = `${API_BASE}/${USERNAME}/cloudcasts/?limit=100`;
        while (nextUrl) {
          const res = await fetch(nextUrl, { cache: 'no-store' });
          if (!res.ok) throw new Error('Failed to fetch cloudcasts');
          const page = await res.json();
          cloudcasts = cloudcasts.concat(page.data || []);
          nextUrl = page.paging?.next || null;
          if (cloudcasts.length >= 1000) break;
        }

        if (!isActive) return;

        setAllCloudcasts(cloudcasts);
        setScheduleShows(schedShows);
        hasFetchedRef.current = true;
      } catch (err) {
        if (!isActive) return;
        console.error('Catchup load error:', err);
        setError('Failed to load episodes. Please try again.');
      } finally {
        if (isActive) setIsLoading(false);
      }
    })();

    return () => { isActive = false; };
  }, []);

  /* ---------------- Build show groups ---------------- */
  const showGroups = useMemo(() => {
    if (allCloudcasts.length === 0) return [];

    const groupsByPattern = new Map();
    const orphansByTitle = new Map();

    // Register schedule entries as groups
    scheduleShows.forEach((s) => {
      const pattern = (
        s.matchPattern || deriveMatchPattern(s.title)
      ).toLowerCase().trim();
      if (!pattern) return;

      if (!groupsByPattern.has(pattern)) {
        groupsByPattern.set(pattern, {
          key: pattern,
          title: s.title || pattern,
          pattern,
          schedule: s,
          days: getDaysForScheduleEntry(s),
          artwork: s.art || null,
          episodes: [],
        });
      } else {
        const existing = groupsByPattern.get(pattern);
        existing.days = Array.from(
          new Set([...existing.days, ...getDaysForScheduleEntry(s)])
        );
      }
    });

    // Assign cloudcasts to groups
    allCloudcasts.forEach((cloudcast) => {
      const parsed = parseShow(cloudcast);
      const titleNorm = parsed.title.toLowerCase().trim();
      const episode = { ...cloudcast, parsed };

      // Longest pattern wins so "craig charles" beats "craig"
      const sortedPatterns = Array.from(groupsByPattern.keys()).sort(
        (a, b) => b.length - a.length
      );
      let matchedGroup = null;
      for (const pattern of sortedPatterns) {
        if (pattern && titleNorm.includes(pattern)) {
          matchedGroup = groupsByPattern.get(pattern);
          break;
        }
      }

      if (matchedGroup) {
        matchedGroup.episodes.push(episode);
      } else {
        // Orphan — not in schedule
        const key = `orphan:${titleNorm}`;
        if (!orphansByTitle.has(key)) {
          orphansByTitle.set(key, {
            key,
            title: parsed.title,
            pattern: titleNorm,
            schedule: null,
            days: [],
            artwork: parsed.artwork,
            episodes: [],
          });
        }
        orphansByTitle.get(key).episodes.push(episode);
      }
    });

    // Finalize
    const groups = [...groupsByPattern.values(), ...orphansByTitle.values()]
      .filter((g) => g.episodes.length > 0)
      .map((g) => {
        g.episodes.sort(
          (a, b) =>
            new Date(b.created_time || 0) - new Date(a.created_time || 0)
        );
        if (!g.artwork) g.artwork = g.episodes[0]?.parsed?.artwork || null;
        return g;
      });

    groups.sort((a, b) => a.title.localeCompare(b.title));
    return groups;
  }, [allCloudcasts, scheduleShows]);

  /* ---------------- Days with data ---------------- */
  const availableDays = useMemo(() => {
    const days = new Set(REQUIRED_DAYS);
    showGroups.forEach((g) =>
      g.days.forEach((d) => {
        if (WEEKDAY_ORDER.includes(d)) days.add(d);
      })
    );
    return WEEKDAY_ORDER.filter((d) => days.has(d));
  }, [showGroups]);

  /* ---------------- Filter shows by day + search ---------------- */
  const filteredShows = useMemo(() => {
    let data = showGroups;

    if (selectedDay !== 'all') {
      data = data.filter((g) => g.days.includes(selectedDay));
    }

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase().trim();
      data = data.filter((g) => {
        if (g.title.toLowerCase().includes(q)) return true;
        return g.episodes.some((ep) =>
          (ep.name || '').toLowerCase().includes(q)
        );
      });
    }

    // On the "All Shows" tab, order by most recent episode (newest first).
    // On a specific day, keep alphabetical for easier scanning.
    if (selectedDay === 'all') {
      data = [...data].sort((a, b) => {
        const aTime = a.episodes[0]
          ? new Date(a.episodes[0].created_time || 0).getTime()
          : 0;
        const bTime = b.episodes[0]
          ? new Date(b.episodes[0].created_time || 0).getTime()
          : 0;
        return bTime - aTime;
      });
    }

    return data;
  }, [showGroups, selectedDay, searchQuery]);

  /* ---------------- Restore from URL & auto-select ---------------- */
  useEffect(() => {
    if (isLoading || showGroups.length === 0) return;

    // One-time: honour ?show=<key>
    if (!restoredShowRef.current) {
      const showParam = searchParams.get('show');
      if (showParam) {
        const found =
          showGroups.find((g) => g.key === showParam) ||
          showGroups.find(
            (g) =>
              g.key.includes(showParam) ||
              showParam.includes(g.key) ||
              g.title.toLowerCase().includes(showParam.toLowerCase())
          );

        if (found) {
          if (selectedShowKey !== found.key) setSelectedShowKey(found.key);
          if (selectedDay !== 'all' && !found.days.includes(selectedDay)) {
            setSelectedDay(found.days[0] || 'all');
          }
          restoredShowRef.current = true;
          return;
        }
      }
      restoredShowRef.current = true;
    }

    // Auto-select first show in current filter
    if (filteredShows.length === 0) {
      if (selectedShowKey !== null) setSelectedShowKey(null);
      return;
    }
    const stillVisible = filteredShows.some((g) => g.key === selectedShowKey);
    if (!stillVisible) {
      setSelectedShowKey(filteredShows[0].key);
    }
  }, [
    isLoading,
    showGroups,
    filteredShows,
    searchParams,
    selectedShowKey,
    selectedDay,
  ]);

  /* ---------------- Sync state to URL ---------------- */
  useEffect(() => {
    const params = new URLSearchParams();
    if (selectedDay !== 'all') params.set('day', selectedDay);
    if (selectedShowKey) params.set('show', selectedShowKey);
    if (playingKey) params.set('ep', playingKey);
    setSearchParams(params, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDay, selectedShowKey, playingKey]);

  /* ---------------- Restore episode from URL ---------------- */
  useEffect(() => {
    if (isLoading || allCloudcasts.length === 0) return;
    if (restoredEpRef.current) return;

    const epKey = searchParams.get('ep');
    if (!epKey) return;

    restoredEpRef.current = true;
    const found = allCloudcasts.find((c) => c.key === epKey);
    if (!found) return;

    const parsed = parseShow(found);
    setPlayingKey(found.key);
    loadMixcloudEpisode(
      found.key,
      found.name,
      parsed.title,
      parsed.artwork,
      true
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, allCloudcasts]);

  /* ---------------- Handlers ---------------- */
  const handleSelectDay = (day) => {
    setSelectedDay(day);
  };

  const handleSelectShow = (key) => {
    setSelectedShowKey(key);
  };

  const handlePlayEpisode = useCallback(
    async (episode) => {
      setPlayingKey(episode.key);
      try {
        await loadMixcloudEpisode(
          episode.key,
          episode.name,
          episode.parsed.title,
          episode.parsed.artwork,
          true
        );
      } catch (err) {
        console.error('Play error:', err);
      }
    },
    [loadMixcloudEpisode]
  );

  const handleShareEpisode = useCallback(
    async (episode) => {
      const params = new URLSearchParams();
      if (selectedDay !== 'all') params.set('day', selectedDay);
      if (selectedShowKey) params.set('show', selectedShowKey);
      params.set('ep', episode.key);
      const url = `${window.location.origin}/catchup?${params.toString()}`;

      if (navigator.share) {
        try {
          await navigator.share({
            title: episode.name,
            text: `Listen to "${episode.name}" on Utter Radio`,
            url,
          });
          return;
        } catch (err) {
          if (err.name === 'AbortError') return;
        }
      }
      try {
        await navigator.clipboard.writeText(url);
      } catch {
        // Ignore copy failures silently
      }
    },
    [selectedDay, selectedShowKey]
  );

  const selectedShow = useMemo(
    () => filteredShows.find((g) => g.key === selectedShowKey) || null,
    [filteredShows, selectedShowKey]
  );

  /* ---------------- Render ---------------- */
  return (
    <div className="pageContainer">
      <section className="news-page-header">
        <h1>Catchup</h1>
        <p>Browse all previous episodes by show.</p>
      </section>

      <section className="catchup-player-container">
        {/* Search */}
        <div className="catchup-search-wrap">
          <div className="catchup-search-bar">
            <input
              className="catchup-search-input"
              placeholder="SEARCH SHOWS OR EPISODES"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            {searchQuery ? (
              <button
                type="button"
                className="catchup-search-submit"
                onClick={() => setSearchQuery('')}
                aria-label="Clear search"
              >
                <CloseIcon />
              </button>
            ) : (
              <span className="catchup-search-icon-static" aria-hidden>
                <SearchIcon />
              </span>
            )}
          </div>
        </div>

        {/* Day tabs */}
        <div className="catchup-tabs">
          <button
            type="button"
            className={`catchup-tab ${selectedDay === 'all' ? 'active' : ''}`}
            onClick={() => handleSelectDay('all')}
          >
            <span className="catchup-tab-name">All</span>
            <span className="catchup-tab-date">Shows</span>
          </button>
          {availableDays.map((day) => (
            <button
              key={day}
              type="button"
              className={`catchup-tab ${selectedDay === day ? 'active' : ''}`}
              onClick={() => handleSelectDay(day)}
            >
              <span className="catchup-tab-name">
                {DAY_LABELS[day].slice(0, 3)}
              </span>
              <span className="catchup-tab-date">{DAY_LABELS[day]}</span>
            </button>
          ))}
        </div>

        {/* Drilldown */}
        {isLoading ? (
          <div className="catchup-loading">
            <div className="spinner" />
            <p>Loading shows…</p>
          </div>
        ) : error ? (
          <div className="catchup-empty">{error}</div>
        ) : filteredShows.length === 0 ? (
          <div className="catchup-empty">
            {searchQuery
              ? `No shows matching "${searchQuery}"`
              : `No shows scheduled for ${
                  DAY_LABELS[selectedDay] || 'this day'
                }`}
          </div>
        ) : (
          <div className="catchup-drilldown">
            {/* Shows sidebar */}
            <aside className="catchup-shows-sidebar">
              <div className="catchup-shows-header">
                <span>
                  {selectedDay === 'all'
                    ? 'All Shows'
                    : DAY_LABELS[selectedDay]}
                </span>
                <span className="catchup-shows-count">
                  {filteredShows.length}
                </span>
              </div>
              <ul className="catchup-shows-list">
                {filteredShows.map((show) => (
                  <li
                    key={show.key}
                    className={`catchup-show-item ${
                      show.key === selectedShowKey ? 'active' : ''
                    }`}
                    onClick={() => handleSelectShow(show.key)}
                  >
                    <img
                      src={
                        show.artwork || BRANDS[BRANDS.length - 1].url
                      }
                      alt={show.title}
                      className="catchup-show-img"
                      onError={(e) => {
                        e.currentTarget.onerror = null;
                        e.currentTarget.src =
                          BRANDS[BRANDS.length - 1].url;
                      }}
                    />
                    <div className="catchup-show-info">
                      <h4 className="catchup-show-title">
                        {show.title}
                      </h4>
                      <p className="catchup-show-count">
                        {show.episodes.length}{' '}
                        {show.episodes.length === 1
                          ? 'episode'
                          : 'episodes'}
                        {show.days.length > 0 && (
                          <span className="catchup-show-days">
                            {' · '}
                            {show.days
                              .map((d) => DAY_LABELS[d].slice(0, 3))
                              .join(', ')}
                          </span>
                        )}
                      </p>
                    </div>
                  </li>
                ))}
              </ul>
            </aside>

            {/* Episodes panel */}
            <div className="catchup-episodes-panel">
              {selectedShow ? (
                <>
                  <div className="catchup-episodes-header">
                    <h3>{selectedShow.title}</h3>
                    <span className="catchup-episodes-count">
                      {selectedShow.episodes.length} episodes
                    </span>
                  </div>
                  <ul className="catchup-episodes-list">
                    {selectedShow.episodes.map((ep) => {
                      const isThisPlaying =
                        playingKey === ep.key &&
                        globalIsPlaying &&
                        isCatchupMode;
                      return (
                        <li
                          key={ep.key}
                          className={`catchup-episode-item ${
                            isThisPlaying ? 'playing' : ''
                          }`}
                        >
                          <button
                            type="button"
                            className="catchup-episode-play"
                            onClick={() => handlePlayEpisode(ep)}
                            aria-label={
                              isThisPlaying ? 'Pause' : 'Play'
                            }
                          >
                            <PlayPauseCircle
                              playing={isThisPlaying}
                              size={36}
                            />
                          </button>
                          <div className="catchup-episode-info">
                            <h5 className="catchup-episode-title">
                              {ep.parsed.subtitle ||
                                ep.parsed.dateLabel ||
                                ep.parsed.title}
                            </h5>
                            <p className="catchup-episode-meta">
                              {ep.parsed.dateLabel &&
                                ep.parsed.subtitle &&
                                `${ep.parsed.dateLabel} · `}
                              {formatDuration(ep.audio_length)}
                            </p>
                          </div>
                          <button
                            type="button"
                            className="catchup-episode-share"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleShareEpisode(ep);
                            }}
                            aria-label="Share episode"
                          >
                            <ShareIcon />
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                </>
              ) : (
                <div className="catchup-empty-small">
                  Select a show to see episodes
                </div>
              )}
            </div>
          </div>
        )}
      </section>
    </div>
  );
};